import { typeAvailableSlot } from "../HomeSlice";
import momentTz from "moment-timezone";

export interface AvailabilitySlot {
  calendarUuid: string;
  calendarName: string;
  calendarOpeningTime: string;
  calendarClosingTime: string;
  maxPax: string;
  roomType: string;
  startTime: string;
  endTime: string;
}

export const generateStartTimes = (
  anchorStartDate: string,
  availability: AvailabilitySlot[],
  durationInHours: number,
  pax: number,
  roomType: string
) => {
  const anchorStartDateDDMMYYYY =
    momentTz(anchorStartDate).tz("Asia/Singapore");

  const startTimes = [];
  const intervalInMinutes = 30;

  const startTimeMatchingPaxAndRoomType = availability.filter((avail) => {
    return pax <= parseInt(avail.maxPax) && avail.roomType === roomType;
  });

  startTimeMatchingPaxAndRoomType.sort((a, b) => {
    // Convert maxPax to number for comparison
    let maxPaxA = parseInt(a.maxPax, 10);
    let maxPaxB = parseInt(b.maxPax, 10);

    // Handle possible NaN values by treating them as 0
    if (isNaN(maxPaxA)) maxPaxA = 0;
    if (isNaN(maxPaxB)) maxPaxB = 0;

    return maxPaxA - maxPaxB;
  });

  for (const entry of startTimeMatchingPaxAndRoomType) {
    const {
      calendarUuid,
      calendarName,
      calendarOpeningTime,
      calendarClosingTime,
      maxPax,
      roomType,
      startTime,
      endTime,
    } = entry;
    const startDate = momentTz(startTime).tz("Asia/Singapore");
    const endDate = momentTz(endTime).tz("Asia/Singapore");

    // Validation: prevent incorrect date
    if (endDate.isBefore(startDate)) {
      continue;
    }

    const totalMinutesDifference = endDate.diff(startDate, "minutes");
    const numberOfSlots = totalMinutesDifference / intervalInMinutes;

    for (let i = 0; i < numberOfSlots; i++) {
      const incrementMinutes = i * intervalInMinutes;
      const newStartDate = startDate.clone();
      newStartDate.add(incrementMinutes, "minutes");

      const item = {
        calendarUuid,
        calendarName,
        calendarOpeningTime,
        calendarClosingTime,
        maxPax,
        roomType,
        startTime: newStartDate.toISOString(),
      };

      startTimes.push(item);
    }
  }

  const filteredStartTimes = startTimes.filter((startTimeObj) => {
    const startTimeMoment = momentTz(startTimeObj.startTime).tz("Asia/Singapore");
    const calOpeningTimeMoment = momentTz(startTimeObj.calendarOpeningTime).tz("Asia/Singapore");
    const calClosingTimeMoment = momentTz(startTimeObj.calendarClosingTime).tz("Asia/Singapore");

    // Minus x hours for closing time
    calClosingTimeMoment.subtract(durationInHours, "hours");

    const capacityPax = parseInt(startTimeObj.maxPax);
    const timeslotRoomType = startTimeObj.roomType;

    // Reject: Out of the operating hours
    if (
      startTimeMoment.isBefore(calOpeningTimeMoment) ||
      startTimeMoment.isAfter(calClosingTimeMoment)
    ) {
      return false;
    }

    // Reject: Room size too small
    if (pax > capacityPax) {
      return false;
    }

    // Reject: Room type is different
    if (roomType !== timeslotRoomType) {
      return false;
    }

    // Reject: start time is not on the selected date (dd-mm-yyyy)
    if (startTimeMoment.day() !== anchorStartDateDDMMYYYY.day()) {
      return false;
    }

    return true;
  });

  return filteredStartTimes;
};
